import classNames from 'classnames/bind';
import React, { useContext, useMemo } from 'react';
import { SessionContext } from '../../context/SessionContext';
import { useGetSessions } from '../../hooks/chat/useGetSessions';
import Icon from '../Icon';
import styles from './History.module.scss';
import { groupSessions } from './History.utils';

const cx = classNames.bind(styles);

interface HistoryProps {
  onSelectSession: (sessionId: string) => void;
}

const History = ({ onSelectSession }: HistoryProps): React.ReactElement => {
  const { data } = useGetSessions();
  const { searchTerm, setSearchTerm } = useContext(SessionContext);
  const preparedSearchTerm = searchTerm.toLowerCase();

  const filteredData = useMemo(
    () =>
      data?.filter((session) =>
        session.session_history?.some(
          (item) =>
            item.cb_response?.toLowerCase().indexOf(preparedSearchTerm) >= 0 ||
            item.query?.toLowerCase().indexOf(preparedSearchTerm) >= 0
        )
      ) || [],
    [data, preparedSearchTerm]
  );

  const history = useMemo(() => groupSessions(filteredData), [filteredData]);

  return (
    <div className={cx('body')}>
      <div className={cx('search')}>
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search history"
        />
        <Icon.Search />
      </div>
      {history.length === 0 ? (
        <div>
          {searchTerm
            ? 'Looks like there were no matches for your search. Try using different keywords.'
            : 'No History'}
        </div>
      ) : null}
      {history.map(({ group, sessions }, groupIndex) => (
        <div key={group}>
          <div className={cx('groupTitle')}>
            <div className={cx('groupTitleLine')} />
            <div>{group}</div>
            <div className={cx('groupTitleLine')} />
          </div>
          <div>
            {sessions.map((session, sessionIndex) => {
              const parts = searchTerm
                ? session.session_history[0].query.split(new RegExp(`(${preparedSearchTerm})`, 'ig'))
                : [session.session_history[0].query];

              return (
                <div
                  className={cx('session')}
                  key={session.session_id}
                  data-testid={`btn-selectSession-${groupIndex}-${sessionIndex}`}
                  onClick={() => onSelectSession(session.session_id)}
                >
                  <div className={cx('sessionLabel')}>
                    {parts.map((part, index) => (
                      // eslint-disable-next-line react/no-array-index-key
                      <React.Fragment key={index}>
                        {part.toLowerCase() === preparedSearchTerm ? <span>{part}</span> : part}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

export default History;
